import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { AccountingPeriod } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    formatPeriodLabel,
    formatPeriodRange,
    monthLabelKey,
} from '../../periodsMeta';

type ClosePeriodDialogProps = {
    /** The period being acted on; `null` closes the dialog. */
    period: AccountingPeriod | null;
    /** `true` = reopening rather than closing. */
    isReopen: boolean;
    isPending: boolean;
    errorMessage: string | null;
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Explicit confirmation for closing (or reopening) a single month.
 *
 * Shows what the action affects before it happens: the period, its inclusive
 * date range, and how much is already inside it. Drafts dated in the period get
 * a **warning, not a block** — closing does not delete them, it only means they
 * can no longer be posted where they sit.
 */
export const ClosePeriodDialog: React.FC<ClosePeriodDialogProps> = ({
    period,
    isReopen,
    isPending,
    errorMessage,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal
            isOpen={period !== null}
            onOpenChange={(open) => !open && onCancel()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('periods-close-period-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {isReopen
                                    ? t('periods.closePeriod.reopenTitle')
                                    : t('periods.closePeriod.title')}
                            </Modal.Heading>
                            <p className="text-sm text-muted">
                                {isReopen
                                    ? t('periods.closePeriod.reopenSubtitle')
                                    : t('periods.closePeriod.subtitle')}
                            </p>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4 pb-2">
                            {period ? (
                                <>
                                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl bg-surface-secondary p-4">
                                        <Fact
                                            label={t(
                                                'periods.closePeriod.period',
                                            )}
                                            value={formatPeriodLabel(
                                                period.yearMonth,
                                                t(
                                                    monthLabelKey(
                                                        period.yearMonth,
                                                    ),
                                                ),
                                            )}
                                        />
                                        <Fact
                                            label={t(
                                                'periods.closePeriod.range',
                                            )}
                                            value={formatPeriodRange(period)}
                                        />
                                        <Fact
                                            label={t(
                                                'periods.closePeriod.entries',
                                            )}
                                            value={String(period.entryCount)}
                                        />
                                        <Fact
                                            label={t(
                                                'periods.closePeriod.total',
                                            )}
                                            value={
                                                period.postedTotalTetri > 0
                                                    ? formatGel(
                                                          period.postedTotalTetri,
                                                      )
                                                    : '–'
                                            }
                                        />
                                    </dl>

                                    {!isReopen && period.draftCount > 0 ? (
                                        <p
                                            className="flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground"
                                            {...getNodeTestIdProp(
                                                'periods-close-period-draft-warning',
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                className="mt-0.5 shrink-0"
                                                icon="gravity-ui:triangle-exclamation"
                                            />
                                            {t(
                                                'periods.closePeriod.draftWarning',
                                                { count: period.draftCount },
                                            )}
                                        </p>
                                    ) : null}

                                    {errorMessage ? (
                                        <p
                                            className="text-sm text-danger"
                                            {...getNodeTestIdProp(
                                                'periods-close-period-error',
                                            )}
                                        >
                                            {errorMessage}
                                        </p>
                                    ) : null}
                                </>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'periods-close-period-cancel',
                                )}
                            >
                                {t('periods.actions.cancel')}
                            </Button>
                            <Button
                                isPending={isPending}
                                onPress={onConfirm}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'periods-close-period-confirm',
                                )}
                            >
                                {isReopen
                                    ? t('periods.actions.reopenPeriod')
                                    : t('periods.actions.closePeriod')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <dt className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </dt>
        <dd className="text-sm tabular-nums text-foreground">{value}</dd>
    </div>
);
